export const formatTimestamp = (ts?: number): string => {
  return ts
    ? `${new Date(ts).toLocaleDateString()} ${new Date(ts).toLocaleTimeString()}`
    : '-';
};

/**
 * Formats an ISO timestamp string (UTC) to local time display
 * Handles both ISO strings with/without 'Z' suffix and ensures local timezone display
 */
export const formatTimestampLocal = (timestamp?: string): string => {
  if (!timestamp) return '-';

  try {
    // Ensure the timestamp is treated as UTC by appending 'Z' if not present
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';

    // Create Date object from UTC ISO string - JavaScript automatically converts UTC to local time
    const date = new Date(utcTimestamp);

    // Verify the date is valid
    if (isNaN(date.getTime())) {
      return '-';
    }

    // Return formatted local time
    return date.toLocaleString();
  } catch (error) {
    console.warn('Invalid timestamp format:', timestamp, error);
    return '-';
  }
};

/**
 * Formats an ISO timestamp string to a more compact local time display
 * Shows date and time in a shorter format
 */
export const formatTimestampCompact = (timestamp?: string): string => {
  if (!timestamp) return '-';

  try {
    // Ensure the timestamp is treated as UTC by appending 'Z' if not present
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
    if (isNaN(date.getTime())) {
      return '-';
    }

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch (error) {
    console.warn('Invalid timestamp format:', timestamp, error);
    return '-';
  }
};

/**
 * Calculate and format the duration between two timestamps
 * @param startedAt - Start timestamp (ISO string)
 * @param lastUpdated - End timestamp (ISO string)
 * @returns Formatted duration string (e.g., "2 days 3 hours", "5 hours 30 minutes", "45 minutes")
 */
export const calculateDuration = (startedAt: string, lastUpdated: string): string => {
  const start = new Date(startedAt);
  const end = new Date(lastUpdated);
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ${diffHours % 24} hour${(diffHours % 24) !== 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMins % 60} minute${(diffMins % 60) !== 1 ? 's' : ''}`;
  } else {
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  }
};
